import React from 'react';
import styled from 'styled-components';

// Props interface
interface CartActionsProps {
  onCompleteSale: () => void;
  onBack: () => void;
  itemsCount: number;
  t: (key: string) => string;
}

// Styled Components
const ButtonsCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.06);
  border: 1px solid #e6edf5;
  padding: 1.5rem;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    border-radius: 20px;
    padding: 1.25rem;
    margin-top: 1.25rem;
  }

  @media (max-width: 480px) {
    border-radius: 16px;
    padding: 1rem;
    margin-top: 1rem;
  }

  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  }
`;

const ButtonsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 480px) {
    gap: 0.6rem;
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  background: #374151;
  color: white;
  border: none;
  border-radius: 14px;
  padding: 1.25rem;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);

  @media (max-width: 768px) {
    padding: 1.1rem;
    font-size: 1rem;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 0.95rem;
    font-size: 0.95rem;
    border-radius: 12px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.22);
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #4b5563;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  background: transparent;
  color: #111827;
  border: 1.5px solid #cbd5e1;
  border-radius: 14px;
  padding: calc(1.25rem - 1.5px);
  font-size: 1.05rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 0.75rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #0f172a;
  }

  &:active:not(:disabled) {
    background-color: #e5e7eb;
    border-color: #cbd5e1;
  }

  &:disabled {
    color: #9ca3af;
    border-color: #e5e7eb;
    background: transparent;
  }

  .dark & {
    color: #e5e7eb;
    border-color: #2e3441;
  }
  .dark &:hover:not(:disabled) {
    background-color: rgba(148, 163, 184, 0.06);
    border-color: #3a4250;
    color: #e5e7eb;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1rem;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 0.9rem;
    font-size: 0.95rem;
    border-radius: 12px;
    border-width: 1.25px;
    margin-top: 0.6rem;
  }
`;

const QuoteRequestSection = styled.div`
  margin-top: 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
  }
`;

const QuoteRequestButton = styled.button`
  background: transparent;
  border: none;
  color: #8e8e93;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: not-allowed;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f7;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.45rem 0.85rem;
    border-radius: 8px;
    gap: 0.4rem;
  }
`;

const ComingSoonBadge = styled.span`
  background-color: #2c2c2e;
  color: #e5e5ea;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.25rem 0.6rem;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 480px) {
    font-size: 0.65rem;
    padding: 0.22rem 0.5rem;
    border-radius: 7px;
  }
`;

// Main Component
const CartActions: React.FC<CartActionsProps> = ({
  onCompleteSale,
  onBack,
  itemsCount,
  t
}) => {
  return (
    <ButtonsCard>
      <ButtonsSection>
        <PrimaryButton onClick={onCompleteSale} disabled={itemsCount === 0}>
          {t('nextStep')}
        </PrimaryButton>
        <SecondaryButton onClick={onBack}>
          {t('sellAnotherDevice')}
        </SecondaryButton>
      </ButtonsSection>
      <QuoteRequestSection>
        <QuoteRequestButton disabled>
          <span>{t('requestQuote')}</span>
          <ComingSoonBadge>{t('comingSoon')}</ComingSoonBadge>
        </QuoteRequestButton>
      </QuoteRequestSection>
    </ButtonsCard>
  );
};

export default CartActions;
